import { useState, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useIntl } from "react-intl";

import "./CreateParcelAccountAgreementModal.scss";

import Modal from "components/v2/Modal/Modal";
import Button from "components/v2/Button/Button";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FEDEX_EULA_PDF_URL = "/documents/FSM_End_User_License_Agreement.pdf";

interface CreateParcelAccountAgreementModalProps {
  show: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

function CreateParcelAccountAgreementModal({ show, onAccept, onDecline }: CreateParcelAccountAgreementModalProps) {
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  const handleDocumentLoadSuccess = ({ numPages: totalPages }: { numPages: number }) => {
    setNumPages(totalPages);
  };

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Consider scrolled to end if within 20px of bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

    if (isAtBottom && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
    }
  }, [hasScrolledToEnd]);

  const handleClose = () => {
    setHasScrolledToEnd(false);
    onDecline();
  };

  const handleAccept = () => {
    setHasScrolledToEnd(false);
    onAccept();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header>
        <div className="text-xl font-semibold text-primary">
          {intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.eulaModalTitle",
            defaultMessage: "FedEx End-User License Agreement",
          })}
        </div>
      </Modal.Header>
      <Modal.Body className="eula-modal__body">
        <div ref={scrollContainerRef} className="eula-modal__pdf-container" onScroll={handleScroll}>
          <Document file={FEDEX_EULA_PDF_URL} onLoadSuccess={handleDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={660}
              />
            ))}
          </Document>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end gap-lg">
          <Button
            variant="secondary-gray"
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.eulaDecline",
              defaultMessage: "I decline",
            })}
            onClick={handleClose}
          />
          <Button
            variant="primary"
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.eulaAccept",
              defaultMessage: "I have read and accept the terms",
            })}
            onClick={handleAccept}
            disabled={!hasScrolledToEnd}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default CreateParcelAccountAgreementModal;
